/**
 * workoutSteps — flatten a workout's blocks into an ordered list of
 * "steps" the Athlete Runner walks through.
 *
 * Phase 2 (2026-02-26) — supersets/circuits interleaving:
 *   - Standalone blocks (1 exercise): every set runs back-to-back like before.
 *   - Superset / circuit blocks (2+ exercises with `link_type` set):
 *     interleave by ROUND. The round number is the set's `set_num`, so a
 *     coach can declare "this slot only runs in rounds 1 and 3" simply by
 *     omitting `set_num` 2 from that slot's sets_data.
 *
 * For round R in ascending order:
 *   for slot S in slot order:
 *     if slot S has a set with set_num === R → emit a step for it
 *
 * Data shape this function consumes (from the backend `/workouts/{id}` hydrator):
 *   workout.blocks[i] = {
 *     id, name, block_group, link_type: null | "superset" | "circuit",
 *     exercises: [
 *       { slot: 1, levels: [{ level, exercise_name, exercise_id, sets_data: [{set_num, percent_max, reps, rest_mins}, ...], ... }] },
 *       ...
 *     ],
 *     // legacy `levels` mirror exists but we always read `exercises` here
 *   }
 *
 * `selectedLevels[blockIdx_slotIdx]` lets the runner pick a level per
 * exercise slot. Default to index 0 if not set. (Multi-level UX itself is
 * not in scope for Phase 2; the structure is here so it's not a big
 * change later.)
 */

const LINKED_TYPES = new Set(["superset", "circuit"]);

const slotLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function _blockLetter(block, blockIdx) {
    // Prefer the explicit `block_group` (single-letter convention "A","B","C")
    // and fall back to the alphabetical position of the block in the workout.
    const g = (block?.block_group || "").trim();
    if (g) return g.charAt(0).toUpperCase();
    return slotLetters[blockIdx % slotLetters.length];
}

function _slotLabel({ block, blockIdx, slotIdx, isLinkedBlock }) {
    const letter = _blockLetter(block, blockIdx);
    return isLinkedBlock ? `${letter}${slotIdx + 1}` : letter;
}

function _levelForSlot(slot, selectedLevels, blockIdx, slotIdx) {
    if (!slot || !Array.isArray(slot.levels) || slot.levels.length === 0) return null;
    const key = `${blockIdx}_${slotIdx}`;
    const requested = selectedLevels?.[key] ?? selectedLevels?.[blockIdx] ?? 0;
    return slot.levels[requested] || slot.levels[0];
}

/**
 * @param {object} workout — hydrated workout doc
 * @param {object} [selectedLevels] — optional {blockIdx_slotIdx: levelIdx} map
 * @returns {Array<Step>} ordered steps the runner walks through
 */
export function buildSteps(workout, selectedLevels = {}) {
    const out = [];
    if (!workout || !Array.isArray(workout.blocks)) return out;

    for (let blockIdx = 0; blockIdx < workout.blocks.length; blockIdx += 1) {
        const block = workout.blocks[blockIdx];
        const slots = Array.isArray(block.exercises) && block.exercises.length > 0
            ? block.exercises
            : (Array.isArray(block.levels) && block.levels.length > 0
                ? [{ slot: 1, levels: block.levels }]
                : []);
        if (slots.length === 0) continue;

        const isLinkedBlock = LINKED_TYPES.has(block.link_type) && slots.length >= 2;
        const stepsForBlock = [];

        if (!isLinkedBlock) {
            // Standalone — walk each slot's level[0].sets_data top-to-bottom.
            // (If somehow a non-linked block has multiple slots, walk each
            // slot's sets back-to-back rather than dropping the extra slots.)
            slots.forEach((slot, slotIdx) => {
                const level = _levelForSlot(slot, selectedLevels, blockIdx, slotIdx);
                if (!level || !Array.isArray(level.sets_data)) return;
                level.sets_data.forEach((set, setIdx) => {
                    stepsForBlock.push({
                        blockIdx,
                        slotIdx,
                        levelIdx: 0,
                        setIdx,
                        round: Number(set.set_num) || setIdx + 1,
                        isLinkedBlock: false,
                        linkType: block.link_type || null,
                        block,
                        slot,
                        level,
                        set,
                        slotLabel: _slotLabel({ block, blockIdx, slotIdx, isLinkedBlock: false }),
                    });
                });
            });
        } else {
            // Superset / circuit — interleave by round.
            // 1) collect every unique set_num across slots (= round numbers)
            const roundSet = new Set();
            slots.forEach((slot, slotIdx) => {
                const level = _levelForSlot(slot, selectedLevels, blockIdx, slotIdx);
                if (!level || !Array.isArray(level.sets_data)) return;
                level.sets_data.forEach((s) => {
                    const r = Number(s.set_num);
                    if (Number.isFinite(r) && r > 0) roundSet.add(r);
                });
            });
            const rounds = Array.from(roundSet).sort((a, b) => a - b);

            // 2) walk rounds, then slots within each round
            rounds.forEach((round) => {
                slots.forEach((slot, slotIdx) => {
                    const level = _levelForSlot(slot, selectedLevels, blockIdx, slotIdx);
                    if (!level || !Array.isArray(level.sets_data)) return;
                    // Find the set with this set_num.
                    const setIdx = level.sets_data.findIndex(
                        (s) => Number(s.set_num) === round,
                    );
                    if (setIdx === -1) return; // slot skips this round
                    const set = level.sets_data[setIdx];
                    stepsForBlock.push({
                        blockIdx,
                        slotIdx,
                        levelIdx: 0,
                        setIdx,
                        round,
                        isLinkedBlock: true,
                        linkType: block.link_type,
                        block,
                        slot,
                        level,
                        set,
                        slotLabel: _slotLabel({ block, blockIdx, slotIdx, isLinkedBlock: true }),
                    });
                });
            });
        }

        // Stamp total counts + in-block index for UI.
        const totalSetsInBlock = stepsForBlock.length;
        const totalRounds = isLinkedBlock
            ? new Set(stepsForBlock.map((s) => s.round)).size
            : totalSetsInBlock;
        stepsForBlock.forEach((s, i) => {
            out.push({ ...s, stepInBlockIdx: i, totalSetsInBlock, totalRounds });
        });
    }
    return out;
}

/**
 * Is the next step starting a new block? (Drives the "strength → wheel"
 * transition: when the current step is the last for its block, the runner
 * pops the wheel before advancing.)
 */
export function isLastStepInBlock(steps, idx) {
    if (idx < 0 || idx >= steps.length) return false;
    const cur = steps[idx];
    const next = steps[idx + 1];
    if (!next) return true; // last step overall
    return next.blockIdx !== cur.blockIdx;
}

/** Number of unique driver exercises across all steps — used to gate maxes. */
export function uniqueDrivers(steps, exerciseMap = {}) {
    const seen = new Map();
    for (const s of steps) {
        if (!s.level) continue;
        const id = s.level.based_off_exercise_id || s.level.exercise_id;
        if (!id || seen.has(id)) continue;
        const name = s.level.based_off_exercise_id
            ? (exerciseMap[s.level.based_off_exercise_id]?.exercise_name || s.level.based_off_exercise_id)
            : s.level.exercise_name;
        seen.set(id, { exercise_id: id, exercise_name: name });
    }
    return Array.from(seen.values());
}
